import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { 
  TruckIcon,
  CalendarIcon,
  DocumentTextIcon,
  MapPinIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { DeliveryNote, DeliveryNoteStatus } from '../../types/deliveryNote';
import Modal from '../common/Modal';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatCurrency } from '../../utils/formatters';

interface DeliveryNoteFormProps {
  deliveryNote: DeliveryNote | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (deliveryNoteId: string, data: DeliveryNoteFormData) => void;
}

interface DeliveryNoteFormData {
  status: DeliveryNoteStatus;
  deliveryDate: string;
  shippingDate?: string;
  expectedDeliveryDate?: string;
  deliveryAddress: string;
  deliveryInstructions?: string;
  trackingNumber?: string;
  carrierName?: string;
  shippingMethod?: string;
  driverName?: string;
  vehiclePlateNumber?: string;
  notes?: string;
}

const DELIVERY_NOTE_STATUSES = [
  { value: 'draft', label: 'Piszkozat' },
  { value: 'pending', label: 'Függőben' },
  { value: 'shipped', label: 'Szállítás alatt' },
  { value: 'delivered', label: 'Kiszállítva' },
  { value: 'confirmed', label: 'Megerősítve' },
  { value: 'cancelled', label: 'Törölve' }
] as const;

const SHIPPING_METHODS = [
  { value: 'courier', label: 'Futárszolgálat' },
  { value: 'post', label: 'Posta' },
  { value: 'pickup', label: 'Személyes átvétel' },
  { value: 'own_delivery', label: 'Saját szállítás' },
  { value: 'other', label: 'Egyéb' }
] as const;

const DeliveryNoteForm: React.FC<DeliveryNoteFormProps> = ({ deliveryNote, isOpen, onClose, onSave }) => {
  const { t } = useLanguage();
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<DeliveryNoteFormData>();

  useEffect(() => {
    if (deliveryNote) {
      reset({
        status: deliveryNote.status,
        deliveryDate: deliveryNote.deliveryDate,
        shippingDate: deliveryNote.shippingDate,
        expectedDeliveryDate: deliveryNote.expectedDeliveryDate,
        deliveryAddress: deliveryNote.deliveryAddress,
        deliveryInstructions: deliveryNote.deliveryInstructions,
        trackingNumber: deliveryNote.trackingNumber,
        carrierName: deliveryNote.carrierName,
        shippingMethod: deliveryNote.shippingMethod,
        driverName: deliveryNote.driverName,
        vehiclePlateNumber: deliveryNote.vehiclePlateNumber,
        notes: deliveryNote.notes
      });
    }
  }, [deliveryNote, reset]);

  const onSubmit = (data: DeliveryNoteFormData) => {
    if (!deliveryNote) return;
    onSave(deliveryNote.id, data);
    onClose();
  };

  if (!deliveryNote) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Szállítólevél szerkesztése - ${deliveryNote.number}`}
      size="6xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 p-6">
        {/* Alapinformációk */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <TruckIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítólevél információk</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Szállítólevél száma:</span>
                  <span>{deliveryNote.number}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Ügyfél:</span>
                  <span>{deliveryNote.customerName}</span>
                </div>
                {deliveryNote.totalAmount && (
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700 dark:text-gray-300">Összeg:</span>
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {formatCurrency(deliveryNote.totalAmount, deliveryNote.currency)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Státusz */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Státusz kezelése</h3>
          </div>
          <div className="p-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Státusz *
                </label>
                <select
                  {...register('status', { required: 'A státusz megadása kötelező' })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  {DELIVERY_NOTE_STATUSES.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
                {errors.status && (
                  <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dátumok */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <CalendarIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítási dátumok</h3>
          </div>
          <div className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Szállítási dátum *
                  </label>
                  <input
                    type="date"
                    {...register('deliveryDate', { required: 'A szállítási dátum kötelező' })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                  {errors.deliveryDate && (
                    <p className="mt-1 text-sm text-red-600">{errors.deliveryDate.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Feladási dátum
                  </label>
                  <input
                    type="date"
                    {...register('shippingDate')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Várható érkezés
                  </label>
                  <input
                    type="date"
                    {...register('expectedDeliveryDate')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Szállítási adatok */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <MapPinIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Szállítási információk</h3>
          </div>
          <div className="p-4">
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Szállítási cím *
                </label>
                <textarea
                  {...register('deliveryAddress', { required: 'A szállítási cím kötelező' })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Szállítási cím..."
                />
                {errors.deliveryAddress && (
                  <p className="mt-1 text-sm text-red-600">{errors.deliveryAddress.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Szállítási mód
                  </label>
                  <select
                    {...register('shippingMethod')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  >
                    <option value="">Válasszon...</option>
                    {SHIPPING_METHODS.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Fuvarozó
                  </label>
                  <input
                    type="text"
                    {...register('carrierName')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Fuvarozó neve..."
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Csomagszám
                  </label>
                  <input
                    type="text"
                    {...register('trackingNumber')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Csomagkövetési szám..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sofőr neve
                  </label>
                  <input
                    type="text"
                    {...register('driverName')}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Sofőr neve..."
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rendszám
                </label>
                <input
                  type="text"
                  {...register('vehiclePlateNumber')}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Jármű rendszáma..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Szállítási utasítások
                </label>
                <textarea
                  {...register('deliveryInstructions')}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Szállítási utasítások..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Megjegyzések */}
        <div className="crm-card">
          <div>
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-[var(--crm-border)] bg-[var(--crm-bg)] rounded-t-[10px]">
            <DocumentTextIcon className="w-4 h-4 text-gray-400" />
            <h3 className="text-[13px] font-semibold text-gray-900 dark:text-gray-50">Megjegyzések</h3>
          </div>
          <div className="p-4">
              <div>
                <textarea
                  {...register('notes')}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Megjegyzések..."
                />
              </div>
            </div>
          </div>
        </div>

        {/* Form gombok */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Mégse
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Mentés
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DeliveryNoteForm; 